import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { BookmarkAddedOutlined as BookmarkAddedOutlinedIcon } from '@mui/icons-material';
import { CelebrationOutlined as CelebrationOutlinedIcon } from '@mui/icons-material';
import { Person as PersonIcon } from '@mui/icons-material';
import { Schedule as ScheduleIcon } from '@mui/icons-material';
import { CalendarToday as CalendarTodayIcon } from '@mui/icons-material';
import { AttachMoney as AttachMoneyIcon } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { AccountBalance as AccountBalanceIcon } from '@mui/icons-material';
import { WbSunny as WbSunnyIcon } from '@mui/icons-material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useGlobalConfig } from 'context/GlobalContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { PilotApis } from 'apis/pilotAPI';
import { CustomerDataApis } from 'apis/customerDataAPIs';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId, selectPilotList } from 'store/dataReducers/pilotsSlice';

interface ExtractionStatus {
  weatherData: 'loading' | 'success' | 'error' | 'not_started';
  bcSchedule: 'loading' | 'success' | 'error' | 'not_started';
  userDetails: 'loading' | 'success' | 'error' | 'not_started';
  rawData: 'loading' | 'success' | 'error' | 'not_started';
  bbUserProgram: 'loading' | 'success' | 'error' | 'not_started';
  billingData: 'loading' | 'success' | 'error' | 'not_started';
}

interface ExtractionResult {
  weatherData?: any;
  bcSchedule?: any;
  userDetails?: any;
  rawData?: any;
  bbUserProgram?: any;
  billingData?: any;
}

interface DownloadUrls {
  bcSchedule?: string[];
  userDetails?: string[];
  rawData?: string[];
  bbUserProgram?: string[];
  billingData?: string[];
}

interface ExtractionProgress {
  completed: number;
  total: number;
  percentage: number;
}

export const DataExtractionStatus = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList);
  
  const [extractionStatus, setExtractionStatus] = useState<ExtractionStatus>({
    weatherData: 'not_started',
    bcSchedule: 'not_started',
    userDetails: 'not_started',
    rawData: 'not_started',
    bbUserProgram: 'not_started',
    billingData: 'not_started',
  });
  
  const [extractionResults, setExtractionResults] = useState<ExtractionResult>({});
  const [downloadUrls, setDownloadUrls] = useState<DownloadUrls>({});
  const [extractionProgress, setExtractionProgress] = useState<ExtractionProgress>({
    completed: 0,
    total: 4,
    percentage: 0
  });
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [initialCountdown, setInitialCountdown] = useState(3);
  const [rawDataCountdown, setRawDataCountdown] = useState(10);
  const [downloadingFiles, setDownloadingFiles] = useState<{[key: string]: boolean}>({});

  // Get user ingestion data from localStorage
  const userIngestionDataStr = localStorage.getItem('userIngestionData');
  let userIngestionData = null;
  let uuids: string[] = [];
  let env = '';
  let isBBUser = false;
  let sourceCountry = '';
  let userMeterFuelSelections: Record<string, string[]> = {};

  if (userIngestionDataStr) {
    try {
      userIngestionData = JSON.parse(userIngestionDataStr);
      uuids = userIngestionData.stagedUuids || [];
      env = userIngestionData.selectedEnvironment || 'NA';
      isBBUser = userIngestionData.hasBBUserProgram || false;
      sourceCountry = userIngestionData.sourceCountry || '';
      userMeterFuelSelections = userIngestionData.userMeterFuelSelections || {};
    } catch (parseError) {
      console.error('Failed to parse user ingestion data from localStorage:', parseError);
    }
  }

  const getStatusIcon = (status: 'loading' | 'success' | 'error' | 'not_started') => {
    switch (status) {
      case 'loading':
        return <CircularProgress size={20} />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <Typography color="error" variant="body2">✗</Typography>;
      case 'not_started':
        return <Typography color="text.secondary" variant="body2">⚪</Typography>;
    }
  };

  const getStatusColor = (status: 'loading' | 'success' | 'error' | 'not_started', stepKey?: string) => {
    if (stepKey === 'weatherData' && status === 'error') {
      return theme.palette.warning.main;
    }
    switch (status) {
      case 'loading':
        return theme.palette.info.main;
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'not_started':
        return theme.palette.text.secondary;
    }
  };

  const getStatusText = (status: 'loading' | 'success' | 'error' | 'not_started') => {
    switch (status) {
      case 'loading':
        return 'In Progress';
      case 'success':
        return 'Completed';
      case 'error':
        return 'Failed';
      case 'not_started':
        return 'Not Started';
    }
  };

  const formatErrorMessage = (errorResponse: any): string => {
    if (errorResponse?.error?.details && 
        typeof errorResponse.error.details === 'string' && 
        errorResponse.error.details.includes('503 Service Temporarily Unavailable')) {
      return 'Service Temporarily Unavailable. Please Try later';
    }
    
    if (typeof errorResponse?.error === 'object' && errorResponse.error && 'message' in errorResponse.error) {
      return (errorResponse.error as any).message;
    }
    
    return errorResponse?.error || errorResponse?.message || '';
  };

  const updateExtractionProgress = (completedSteps: number, totalSteps: number = 4) => {
    const percentage = Math.round((completedSteps / totalSteps) * 100);
    setExtractionProgress({
      completed: completedSteps,
      total: totalSteps,
      percentage
    });
  };

  // Utility function to extract s3Urls from API response data
  const extractS3Urls = (data: any): string[] => {
    const urls: string[] = [];
    
    if (Array.isArray(data)) {
      data.forEach((item: any) => {
        if (item.s3Url) {
          urls.push(item.s3Url);
        }
        if (item.data && item.data.s3Url) {
          urls.push(item.data.s3Url);
        }
      });
    } else if (data && typeof data === 'object') {

      if (data.results && typeof data.results === 'object' && !Array.isArray(data.results)) {
        Object.values(data.results).forEach((result: any) => {
          if (result && typeof result === 'object') {
            if (result.data) {
              urls.push(...extractS3Urls(result.data));
            }
            if (result.s3Url) {
              urls.push(result.s3Url);
            }
          }
        });
        return urls;
      }
      
      // Check if the object itself has s3Url (e.g., single BB User Program response)
      if (data.s3Url) {
        urls.push(data.s3Url);
      }
      // Handle nested data structures
      if (data.results && Array.isArray(data.results)) {
        data.results.forEach((item: any) => {
          if (item.s3Url) {
            urls.push(item.s3Url);
          }
          if (item.data && item.data.s3Url) {
            urls.push(item.data.s3Url);
          }
        });
      } else if (data.data && Array.isArray(data.data)) {
        data.data.forEach((item: any) => {
          if (item.s3Url) {
            urls.push(item.s3Url);
          }
          if (item.data && item.data.s3Url) {
            urls.push(item.data.s3Url);
          }
        });
      } else if (data.payload && Array.isArray(data.payload)) {
        data.payload.forEach((item: any) => {
          if (item.s3Url) {
            urls.push(item.s3Url);
          }
          if (item.data && item.data.s3Url) {
            urls.push(item.data.s3Url);
          }
        });
      }
    }
    
    return urls;
  };

  // Utility function to download files from S3 URLs
  const downloadFiles = async (urls: string[], stepKey: string) => {
    if (urls.length === 0) return;
    
    setDownloadingFiles(prev => ({ ...prev, [stepKey]: true }));
    
    try {
      for (const url of urls) {
        const link = document.createElement('a');
        link.href = url;
        link.download = url.split('/').pop() || 'download';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Add a small delay between downloads to avoid overwhelming the browser
        if (urls.length > 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloadingFiles(prev => ({ ...prev, [stepKey]: false }));
    }
  };

  const performExtraction = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get utility name from selected pilot
      if (!selectedPilotId) {
        throw new Error('No pilot selected');
      }

      const projectsResponse = await rc.bffClient.getProjects();
      
      if ('error' in projectsResponse) {
        throw new Error('Failed to fetch projects');
      }

      const projects = projectsResponse.data?.data;
      const selectedProject = projects?.find((project: any) => project.projectId === selectedPilotId);
      
      if (!selectedProject) {
        throw new Error('Selected project not found');
      }

      if (!selectedProject.name) {
        throw new Error('Utility name (project.name) not found for selected pilot');
      }

      const utilityName = selectedProject.name;
      
      // Extract only the first part of the utility name (e.g., "PSEG" from "PSEG UAT")
      const parsedUtilityName = utilityName.split(' ')[0];
      
      const selectedPilot = pilotList.find(pilot => String(pilot.projectId) === String(selectedPilotId));
      
      // Extract destination environment from utility name (e.g., "UAT" from "SORGENIA_UAT")
      const destinationEnv = (selectedPilot as any).environment?.type || '';
      
      // Get all unique meter fuels from userMeterFuelSelections for NSM check
      const allMeterFuels = Object.values(userMeterFuelSelections).flat();
      const uniqueMeterFuels = [...new Set(allMeterFuels)];
      
      // Check if ALL meter types are NSM to determine if raw data should be skipped
      // Skip raw data only if ALL meters are NSM 
      const isNSMMeter = uniqueMeterFuels.length > 0 && uniqueMeterFuels.every((meter: string) => meter.startsWith('NSM'));
      
      // Total steps includes Weather Data + other steps
      let totalSteps = 5; // Default: Weather Data, BC Schedule, User Details, Raw Data, Billing Data
      if (isNSMMeter && isBBUser) {
        totalSteps = 5; // Weather Data, BC Schedule, User Details, BB User Program, Billing Data
      } else if (isNSMMeter && !isBBUser) {
        totalSteps = 4; // Weather Data, BC Schedule, User Details, Billing Data
      } else if (!isNSMMeter && isBBUser) {
        totalSteps = 6; // Weather Data, BC Schedule, User Details, Raw Data, BB User Program, Billing Data
      } else {
        totalSteps = 5; // Weather Data, BC Schedule, User Details, Raw Data, Billing Data
      }
      
      // Show initial loading for 3 seconds only
      setIsInitialLoading(true);
      for (let i = 3; i > 0; i--) {
        setInitialCountdown(i);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      setIsInitialLoading(false);
      
      // Show extraction steps list immediately after countdown
      setExtractionStatus(prev => ({ 
        ...prev,
        weatherData: 'loading',
        bcSchedule: 'not_started',
        userDetails: 'not_started', 
        rawData: 'not_started',
        bbUserProgram: 'not_started',
        billingData: 'not_started' 
      }));
      
      // Step 0: Sync Weather Data
      let weatherDataSuccess = false;
      let weatherDataError = '';
      
      try {
        const weatherDataResponse = await CustomerDataApis.syncWeatherData(rc, {
          source_uuid: uuids,
          source_environment: env,
          destinationEnvironment: destinationEnv,
          destination_pilot_id: selectedPilotId?.toString() || '',
          sourceCountry: sourceCountry || ''
        });
        
        if (weatherDataResponse.success) {
          setExtractionStatus(prev => ({ ...prev, weatherData: 'success' }));
          setExtractionResults(prev => ({ ...prev, weatherData: weatherDataResponse.data }));
          
          updateExtractionProgress(1, totalSteps);
          weatherDataSuccess = true;
        } else {
          const errorMessage = formatErrorMessage(weatherDataResponse) || 'Weather data syncing failed';
          console.warn('Weather data syncing failed:', errorMessage);
          weatherDataError = errorMessage;
          setExtractionStatus(prev => ({ ...prev, weatherData: 'error' }));
          setExtractionResults(prev => ({ ...prev, weatherData: { error: { message: errorMessage } } }));
          updateExtractionProgress(1, totalSteps);
          weatherDataSuccess = true; 
        }
      } catch (error: any) {
        console.warn('Weather data syncing error:', error);
        weatherDataError = error.message || 'Weather data syncing failed';
        setExtractionStatus(prev => ({ ...prev, weatherData: 'error' }));
        setExtractionResults(prev => ({ ...prev, weatherData: { error: { message: weatherDataError } } }));
        updateExtractionProgress(1, totalSteps);
        weatherDataSuccess = true; 
      }
      
      // Step 1: Create Billing Cycle Schedule 
      let bcScheduleSuccess = false;
      let bcScheduleError = '';
      let bcScheduleWarnings: string[] = [];
      
      if (!uuids || uuids.length === 0) {
        bcScheduleError = 'No user UUIDs provided for BC schedule creation';
        setExtractionStatus(prev => ({ ...prev, bcSchedule: 'error' }));
        setError(`Billing Cycle Schedule Error: ${bcScheduleError}`);
        setIsComplete(true);
        return;
      }
      
      const allUuids = uuids.join(',');
      
      const billingCycleResponse = await PilotApis.createBillingCycleSchedule(rc, {
        destinationPilotId: selectedPilotId?.toString() || '',
        destinationEnv: destinationEnv,
        sourceUUID: allUuids,
        sourceEnv: env,
        utilityName: parsedUtilityName
      });
      
      const responseStatus = billingCycleResponse.status || 200;
      const isPartialSuccess = billingCycleResponse.isPartialSuccess === true || responseStatus === 207;
      
      if (billingCycleResponse.success && billingCycleResponse.data) {
        const responseData = billingCycleResponse.data;
        
        if (!responseData.results || !responseData.summary) {
          bcScheduleError = 'Invalid response format: missing results or summary';
          setExtractionStatus(prev => ({ ...prev, bcSchedule: 'error' }));
          setError(`Billing Cycle Schedule Error: ${bcScheduleError}`);
          setIsComplete(true);
          return;
        }
        
        const results = responseData.results;
        const summary = responseData.summary;
        
        const hasAnySuccess = Object.values(results).some((result: any) => 
          result && (result.status === 'exists' || result.status === 'created')
        );
        const hasAnyFailure = Object.values(results).some((result: any) => 
          result && (result.status === 'validation_failed' || result.status === 'creation_failed')
        );
        
        if (hasAnySuccess || responseStatus === 200 || isPartialSuccess) {
          bcScheduleSuccess = true;
          
          setExtractionResults(prev => ({ 
            ...prev, 
            bcSchedule: {
              results: results,
              summary: summary,
              errors: responseData.errors || [],
              warnings: responseData.warnings || []
            }
          }));
          
          const s3Urls = extractS3Urls(responseData);
          if (s3Urls.length > 0) {
            setDownloadUrls(prev => ({ ...prev, bcSchedule: s3Urls }));
          }
          
          if (responseData.warnings && Array.isArray(responseData.warnings)) {
            bcScheduleWarnings.push(...responseData.warnings);
          }
          if (hasAnyFailure) {
            bcScheduleWarnings.push('Some users failed to create billing cycle schedules');
          }
          if (summary.users_without_bc && summary.users_without_bc.length > 0) {
            bcScheduleWarnings.push(`${summary.users_without_bc.length} user(s) without billing cycle codes`);
          }
          
          if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
            console.warn('BC Schedule batch processing errors:', responseData.errors);
            if (isPartialSuccess) {
              bcScheduleWarnings.push(...responseData.errors);
            }
          }
        } else {
          const errorMessages: string[] = [];
          Object.entries(results).forEach(([bcCode, result]: [string, any]) => {
            if (result?.error) {
              errorMessages.push(`BC Code ${bcCode}: ${result.error}`);
            } else if (result?.status) {
              errorMessages.push(`BC Code ${bcCode}: ${result.status}`);
            }
          });
          
          if (responseData.errors && Array.isArray(responseData.errors)) {
            errorMessages.push(...responseData.errors);
          }
          
          bcScheduleError = errorMessages.length > 0 
            ? errorMessages.join('; ')
            : 'All billing cycle schedule creations failed';
        }
      } else {
        const errorMessage = formatErrorMessage(billingCycleResponse) || 'Billing cycle schedule creation failed';
        
        if (responseStatus === 400) {
          bcScheduleError = `Bad request: ${errorMessage}`;
        } else if (responseStatus === 404) {
          bcScheduleError = `No bill cycle codes found: ${errorMessage}`;
        } else if (responseStatus === 500) {
          bcScheduleError = `Server error: ${errorMessage}`;
        } else {
          bcScheduleError = errorMessage;
        }
      }
      
      if (bcScheduleSuccess) {
        setExtractionStatus(prev => ({ ...prev, bcSchedule: 'success' }));
        updateExtractionProgress(2, totalSteps);
        
        if (bcScheduleWarnings.length > 0) {
          console.warn('BC Schedule warnings:', bcScheduleWarnings);
        }
      } else {
        setExtractionStatus(prev => ({ ...prev, bcSchedule: 'error' }));
        console.warn('Billing cycle schedule creation failed');
        setError(`Billing Cycle Schedule Error: ${bcScheduleError}`);
        setIsComplete(true);
        return;
      }

      // Step 2: Fetch User Details 
      setExtractionStatus(prev => ({ ...prev, userDetails: 'loading' }));
      let userDetailsSuccess = false;
      let userDetailsAttempts = 0;
      const maxAttempts = 2;

      while (!userDetailsSuccess && userDetailsAttempts < maxAttempts) {
        userDetailsAttempts++;
        
        const userDetailsResponse = await PilotApis.getUserDetails(rc, {
          userMeterMapping: userMeterFuelSelections,
          utilityName: parsedUtilityName, 
          sourceEnvironment: env,
          sourceUsername: "",
          sourcePassword: "",
          destinationPilotId: selectedPilotId?.toString(),
          destinationEnv: destinationEnv
        });
        if (userDetailsResponse.success) {
          setExtractionStatus(prev => ({ ...prev, userDetails: 'success' }));
          setExtractionResults(prev => ({ ...prev, userDetails: userDetailsResponse.data }));
          
          // Extract and store s3Urls for download
          const s3Urls = extractS3Urls(userDetailsResponse.data);
          if (s3Urls.length > 0) {
            setDownloadUrls(prev => ({ ...prev, userDetails: s3Urls }));
          }
          
          updateExtractionProgress(3, totalSteps);
          userDetailsSuccess = true;
        } else {
          const errorMessage = formatErrorMessage(userDetailsResponse) || 'User details extraction failed';
          
          console.warn(`User Details API attempt ${userDetailsAttempts} failed:`, errorMessage);
          
          if (userDetailsAttempts >= maxAttempts) {
            setExtractionStatus(prev => ({ ...prev, userDetails: 'error' }));
            throw new Error(`User Details Error: ${errorMessage}`);
          } else {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      if (!isNSMMeter) {
        setExtractionStatus(prev => ({ ...prev, rawData: 'loading' }));
        
        // Add 10 second waiting time for raw data API
        for (let i = 10; i > 0; i--) {
          // Update countdown state for UI display
          setRawDataCountdown(i);
          // Update status to show waiting countdown
          setExtractionStatus(prev => ({ ...prev, rawData: 'loading' }));
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        let rawDataSuccess = false;
        let rawDataAttempts = 0;

        while (!rawDataSuccess && rawDataAttempts < maxAttempts) {
          rawDataAttempts++;
          
          const rawDataResponse = await PilotApis.fetchRawData(rc, {
            userMeterMapping: userMeterFuelSelections,
            utilityName: parsedUtilityName, 
            sourceEnvironment: env,
            sourceUsername: "",
            sourcePassword: "",
            destinationPilotId: selectedPilotId?.toString(),
            destinationEnv: destinationEnv
          });
          if (rawDataResponse.success) {
            setExtractionStatus(prev => ({ ...prev, rawData: 'success' }));
            setExtractionResults(prev => ({ ...prev, rawData: rawDataResponse.data }));
            
            // Extract and store s3Urls for download
            const s3Urls = extractS3Urls(rawDataResponse.data);
            if (s3Urls.length > 0) {
              setDownloadUrls(prev => ({ ...prev, rawData: s3Urls }));
            }
            
            updateExtractionProgress(4, totalSteps);
            rawDataSuccess = true;
          } else {
            const errorMessage = formatErrorMessage(rawDataResponse) || 'Raw data extraction failed';
            
            if (rawDataAttempts >= maxAttempts) {
              setExtractionStatus(prev => ({ ...prev, rawData: 'error' }));
              throw new Error(`Raw Data Error: ${errorMessage}`);
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
          }
        }
      } else {
        // Skip raw data for NSM meters - mark as success and update progress
        setExtractionStatus(prev => ({ ...prev, rawData: 'success' }));
        updateExtractionProgress(3, totalSteps); 
      }

      // Step 3: BB User Program (if user is BB user)
      if (isBBUser) {
        setExtractionStatus(prev => ({ ...prev, bbUserProgram: 'loading' }));
        
        let bbUserProgramSuccess = false;
        let bbUserProgramAttempts = 0;

        while (!bbUserProgramSuccess && bbUserProgramAttempts < maxAttempts) {
          bbUserProgramAttempts++;
          
          const envConfig = userIngestionData?.environmentConfig;
          if (!envConfig) {
            throw new Error('Environment configuration not found for BB User Program');
          }

          const bbUserProgramResponse = await CustomerDataApis.fetchBBUserProgram(rc, {
            userUUID: uuids.join(','),
            utilityName: parsedUtilityName,
            sourceEnvironment: env,
            destinationPilotId: selectedPilotId?.toString() || '',
            utilityId: selectedPilotId?.toString() || '',
            hasBBUserProgram: userIngestionData.hasBBUserProgram || false
          });

          if (bbUserProgramResponse.success) {
            setExtractionStatus(prev => ({ ...prev, bbUserProgram: 'success' }));
            setExtractionResults(prev => ({ ...prev, bbUserProgram: bbUserProgramResponse.data }));
            
            const s3Urls = extractS3Urls(bbUserProgramResponse.data);
            if (s3Urls.length > 0) {
              setDownloadUrls(prev => ({ ...prev, bbUserProgram: s3Urls }));
            }
            
            if (isNSMMeter) {
              updateExtractionProgress(4, totalSteps); // For NSM: Weather(1), BC(2), User(3), BB(4)
            } else {
              updateExtractionProgress(5, totalSteps); // For non-NSM: Weather(1), BC(2), User(3), Raw(4), BB(5)
            }
            bbUserProgramSuccess = true;
          } else {
            let errorMessage = 'BB User Program extraction failed';
            
            if (bbUserProgramResponse.data?.errorDetails) {
              errorMessage = bbUserProgramResponse.data.errorDetails;
            } else if (bbUserProgramResponse.data?.message) {
              errorMessage = bbUserProgramResponse.data.message;
            } 
            else if ((bbUserProgramResponse as any).errorDetails) {
              errorMessage = (bbUserProgramResponse as any).errorDetails;
            } else if ((bbUserProgramResponse as any).message) {
              errorMessage = (bbUserProgramResponse as any).message;
            } 
            else {
              errorMessage = formatErrorMessage(bbUserProgramResponse) || errorMessage;
            }
            
            console.warn(`BB User Program API attempt ${bbUserProgramAttempts} failed:`, errorMessage);
            
            if (bbUserProgramAttempts >= maxAttempts) {
              setExtractionStatus(prev => ({ ...prev, bbUserProgram: 'error' }));
              throw new Error(`BB User Program Error: ${errorMessage}`);
            } else {
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
          }
        }
      }

      // Step 4: Fetch Billing Data (FINAL)
      setExtractionStatus(prev => ({ ...prev, billingData: 'loading' }));
      let billingDataSuccess = false;
      let billingDataAttempts = 0;

      while (!billingDataSuccess && billingDataAttempts < maxAttempts) {
        billingDataAttempts++;
        
        const billingDataResponse = await PilotApis.fetchBillingData(rc, {
          userMeterMapping: userMeterFuelSelections,
          utilityName: parsedUtilityName, 
          sourceEnvironment: env,
          sourceUsername: "",
          sourcePassword: "",
          destinationPilotId: selectedPilotId?.toString(),
          destinationEnv: destinationEnv,
          hasBBUserProgram: userIngestionData.hasBBUserProgram || false
        });
        if (billingDataResponse.success) {
          setExtractionStatus(prev => ({ ...prev, billingData: 'success' }));
          setExtractionResults(prev => ({ ...prev, billingData: billingDataResponse.data }));
          
          // Extract and store s3Urls for download
          const s3Urls = extractS3Urls(billingDataResponse.data);
          if (s3Urls.length > 0) {
            setDownloadUrls(prev => ({ ...prev, billingData: s3Urls }));
          }
          
          
          let billingDataStep;
          if (isNSMMeter && isBBUser) {
            billingDataStep = 5; // Weather(1), BC(2), User(3), BB(4), Billing(5)
          } else if (isNSMMeter && !isBBUser) {
            billingDataStep = 4; // Weather(1), BC(2), User(3), Billing(4)
          } else if (!isNSMMeter && isBBUser) {
            billingDataStep = 6; // Weather(1), BC(2), User(3), Raw(4), BB(5), Billing(6)
          } else {
            billingDataStep = 5; // Weather(1), BC(2), User(3), Raw(4), Billing(5)
          }
          
          updateExtractionProgress(billingDataStep, totalSteps);
          billingDataSuccess = true;
        } else {
          const errorMessage = formatErrorMessage(billingDataResponse) || 'Billing data generation failed';
          
          console.warn(`Billing Data API attempt ${billingDataAttempts} failed:`, errorMessage);
          
          if (billingDataAttempts >= maxAttempts) {
            setExtractionStatus(prev => ({ ...prev, billingData: 'error' }));
            throw new Error(`Billing Data API Error: ${errorMessage}`);
          } else {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      // All extractions completed successfully
      setIsComplete(true);

    } catch (error: any) {
      console.error('Extraction error:', error);
      setError(error.message || 'Extraction failed');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only start extraction if we have the pilot list and selected pilot
    if (pilotList.length > 0 && selectedPilotId) {
      performExtraction();
    }
  }, [pilotList, selectedPilotId]);

  const handleBackToUserMetadata = () => {
    navigate(ROUTE_TREE.PROJECT.UPDATE_METADATA.path, { 
      replace: true 
    });
  };

  const handleConfirmUserCreation = () => {
    // Clear localStorage data as we're moving to the next step
    // localStorage.removeItem('userIngestionData');
    // Navigate to User Creation Progress page
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.USER_CREATION_PROGRESS);
  };

  const userIngestionDataStrForUI = localStorage.getItem('userIngestionData');
  let userIngestionDataForUI = null;
  let meterFuelForUI: string[] = [];
  let isBBUserForUI = false;
  
  if (userIngestionDataStrForUI) {
    try {
      userIngestionDataForUI = JSON.parse(userIngestionDataStrForUI);
      // Get all unique meter fuels from userMeterFuelSelections
      const userMeterFuelSelectionsUI = userIngestionDataForUI?.userMeterFuelSelections || {};
      const allMeterFuelsUI = Object.values(userMeterFuelSelectionsUI).flat() as string[];
      meterFuelForUI = allMeterFuelsUI.length > 0 ? [...new Set(allMeterFuelsUI)] : ['ELECTRIC'];
      isBBUserForUI = userIngestionDataForUI.hasBBUserProgram || false;
    } catch (parseError) {
      console.error('Failed to parse user ingestion data from localStorage:', parseError);
    }
  }
  
  const isNSMMeterForUI = meterFuelForUI.length > 0 && meterFuelForUI.every((meter: string) => meter.startsWith('NSM'));
  
  const extractionSteps = [
    { key: 'weatherData', label: 'Weather Data', icon: <WbSunnyIcon />, title: 'Weather Data Syncing', description: 'Syncing weather data for the selected users zipcode.' },
    { key: 'bcSchedule', label: 'BC Schedule', icon: <CalendarTodayIcon />, title: 'Billing Cycle Schedule', description: 'Creating and scheduling billing cycles for the selected users.' },
    { key: 'userDetails', label: 'User Details', icon: <PersonIcon />, title: 'User Details', description: 'Extracting user information, including name, email, and contact details.' },
    ...(isNSMMeterForUI ? [] : [{ key: 'rawData', label: 'Raw Data', icon: <ScheduleIcon />, title: 'Raw Data', description: 'Fetching raw data from the utility provider, including usage history and meter readings.' }]),
    ...(isBBUserForUI ? [{ key: 'bbUserProgram', label: 'BB User Program', icon: <AccountBalanceIcon />, title: 'User Budget Billing Program', description: 'Setting up budget billing program for the selected users.' }] : []),
    { key: 'billingData', label: 'Billing Data', icon: <AttachMoneyIcon />, title: 'Billing Data', description: 'Generating detailed billing reports and financial statements.' }
  ];

  const getTaskBorderColor = (status: 'loading' | 'success' | 'error' | 'not_started', stepKey?: string) => {
    if (stepKey === 'weatherData' && status === 'error') {
      return theme.palette.warning.main;
    }
    switch (status) {
      case 'loading':
        return theme.palette.info.main;
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'not_started':
        return theme.palette.text.secondary;
    }
  };

  const getTaskBackgroundColor = (status: 'loading' | 'success' | 'error' | 'not_started', stepKey?: string) => {
    if (stepKey === 'weatherData' && status === 'error') {
      return theme.palette.warning.light + '20';
    }
    switch (status) {
      case 'loading':
        return theme.palette.info.light + '20';
      case 'success':
        return theme.palette.success.light + '20'; 
      case 'error':
        return theme.palette.error.light + '20'; 
      case 'not_started':
        return theme.palette.background.paper;
    }
  };

  const getTaskIcon = (status: 'loading' | 'success' | 'error' | 'not_started', stepKey?: string) => {
    if (stepKey === 'weatherData' && status === 'error') {
      return <WarningIcon color="warning" />;
    }
    switch (status) {
      case 'loading':
        return <CircularProgress size={20} />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <Typography color="error" variant="body2">✗</Typography>;
      case 'not_started':
        return <Typography color="text.secondary" variant="body2">⚪</Typography>;
    }
  };

  const getWeatherDataMessage = () => {
    if (extractionStatus.weatherData === 'error') {
      const errorMsg = extractionResults.weatherData?.error?.message || 'Weather data syncing failed';
      return `Weather data syncing failed: ${errorMsg}`;
    }
    return `Syncing weather data for the selected users ${sourceCountry ? `in ${sourceCountry} zipcode` : ''}`;
  };

  const getBCScheduleMessage = () => {
    const bcScheduleData = extractionResults.bcSchedule;
    const fileCount = downloadUrls.bcSchedule?.length || 0;
    const fileText = fileCount > 0 ? ` (${fileCount} file${fileCount > 1 ? 's' : ''} available for download)` : '';
    
    if (!bcScheduleData) return `Billing Schedule file created${fileText}`;
    
    if (bcScheduleData.summary && bcScheduleData.results) {
      const { results } = bcScheduleData;
      const bcCodesCount = Object.keys(results || {}).length;
      
      const bcCodeText = bcCodesCount === 1 ? 'BC Code' : 'BC Codes';
      return `Billing Schedule file created for ${bcCodesCount} ${bcCodeText}${fileText}`;
    }
    
    return `Billing Schedule file created${fileText}`;
  };

  const getUserDetailsMessage = () => {
    const fileCount = downloadUrls.userDetails?.length || 0;
    const fileText = fileCount > 0 ? ` (${fileCount} file${fileCount > 1 ? 's' : ''} available for Preview)` : '';
    return `Extracting user information, including name, email, and contact details.${fileText}`;
  };

  const getRawDataMessage = () => {
    const fileCount = downloadUrls.rawData?.length || 0;
    const fileText = fileCount > 0 ? ` (${fileCount} file${fileCount > 1 ? 's' : ''} available for Preview)` : '';
    return `Fetching raw data from the utility provider, including usage history and meter readings.${fileText}`;
  };

  const getBBUserProgramMessage = () => {
    const fileCount = downloadUrls.bbUserProgram?.length || 0;
    const fileText = fileCount > 0 ? ` (${fileCount} file${fileCount > 1 ? 's' : ''} available for Preview)` : '';
    return `Setting up budget billing program for the selected users.${fileText}`;
  };


  const getBillingDataMessage = () => {
    const fileCount = downloadUrls.billingData?.length || 0;
    const fileText = fileCount > 0 ? ` (${fileCount} file${fileCount > 1 ? 's' : ''} available for Preview)` : '';
    return `Generating detailed billing reports and financial statements.${fileText}`;
  };

  return (
    <Box sx={{ p: 3 }}>
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
        mb: 3
        }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Data Extraction Status
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToUserMetadata}
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              px: 3,
              py: 1.5,
              fontSize: '1rem',
            fontWeight: 600,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderColor: theme.palette.primary.main,
              }
            }}
          >
            Back to User Metadata
          </Button>
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
          </Box>
        )}

        {/* Initial Loading State - 3 second loader */}
        {/* {isInitialLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, textAlign: 'center' }}>
            <Box>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                Initializing extraction... Please wait
              </Typography>
            <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                {initialCountdown}
              </Typography>
            </Box>
          </Box>
        )} */}

        {/* No Pilot Selected State */}
        {!isLoading && !selectedPilotId && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            No pilot selected. Please select a pilot from the main navigation.
          </Alert>
        )}

        {/* Pilot List Not Loaded State */}
        {!isLoading && selectedPilotId && pilotList.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Extracting User information...
          </Alert>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Main Card Structure - Show immediately after initial countdown, not after all APIs complete */}
        {!isInitialLoading && (
          <Card sx={{ 
            width: '100%', 
            maxWidth: '800px',
            mx: 'auto',
            borderRadius: 2, 
            border: `1px solid ${theme.palette.primary.main}`,
            bgcolor: 'background.paper', 
            color: 'text.primary', 
            boxShadow: 8 
          }}>
            {/* Header section - equivalent to flex flex-col space-y-1.5 p-6 */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1.5, 
              p: 6 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BookmarkAddedOutlinedIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Extraction Activities
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary">
                The system has extracted and processed data for the selected users.
              </Typography>
            </Box>

            {/* Progress Bar Section - Added similar to UserCreationProgress */}
            <Box sx={{ px: 6, pb: 4 }}>
              <LinearProgress 
                variant="determinate" 
                value={extractionProgress.percentage} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: 4
                  }
                }} 
              />
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 500, 
                  color: 'text.primary',
                  mt: 1,
                  fontSize: '0.95rem',
                  textAlign: 'center'
                }}
              >
                {extractionProgress.completed} of {(() => {
                  if (isNSMMeterForUI && isBBUserForUI) return 5; // Weather, BC, User, BB, Billing
                  if (isNSMMeterForUI && !isBBUserForUI) return 4; // Weather, BC, User, Billing
                  if (!isNSMMeterForUI && isBBUserForUI) return 6; // Weather, BC, User, Raw, BB, Billing
                  return 5; // Weather, BC, User, Raw, Billing
                })()} Tasks completed ({extractionProgress.percentage}%)
              </Typography>
            </Box>

            {/* Main content section - equivalent to p-6 pt-0 space-y-4 min-h-[250px] flex flex-col justify-center */}
            <Box sx={{ 
              p: 6, 
              pt: 0, 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 4, 
              minHeight: '250px', 
              justifyContent: 'center' 
            }}>
              {/* Task List Section - Updated to match UserCreationProgress.tsx style */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {extractionSteps.map((step) => (
                  <Card 
                    key={step.key}
                    sx={{ 
                      border: `2px solid ${getTaskBorderColor(extractionStatus[step.key as keyof ExtractionStatus], step.key)}`,
                      borderRadius: 2,
                      bgcolor: getTaskBackgroundColor(extractionStatus[step.key as keyof ExtractionStatus], step.key),
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                        {/* Task Icon */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 'fit-content' }}>
                          {getTaskIcon(extractionStatus[step.key as keyof ExtractionStatus], step.key)}
                        </Box>

                        {/* Task Content */}
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              {step.icon}
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  fontWeight: 500, 
                                  color: 'text.primary',
                                  fontSize: '0.95rem'
                                }}
                              >
                                {step.title}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {/* Preview Button */}
                              {extractionStatus[step.key as keyof ExtractionStatus] === 'success' && 
                               downloadUrls[step.key as keyof DownloadUrls] && 
                               downloadUrls[step.key as keyof DownloadUrls]!.length > 0 && (
                                <Tooltip title={`Preview ${downloadUrls[step.key as keyof DownloadUrls]!.length} file(s)`}>
                                  <IconButton
                                    size="small"
                                    onClick={() => downloadFiles(downloadUrls[step.key as keyof DownloadUrls]!, step.key)}
                                    disabled={downloadingFiles[step.key]}
                                    sx={{
                                      color: 'primary.main',
                                      '&:hover': {
                                        backgroundColor: 'primary.light',
                                        color: 'primary.dark'
                                      }
                                    }}
                                  >
                                    {downloadingFiles[step.key] ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <VisibilityIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Chip
                                label={getStatusText(extractionStatus[step.key as keyof ExtractionStatus])}
                                size="small"
                                sx={{
                                  backgroundColor: getStatusColor(extractionStatus[step.key as keyof ExtractionStatus], step.key),
                                  color: 'white',
                                  fontWeight: 500,
                                  minWidth: '90px',
                                  fontSize: '0.75rem'
                                }}
                              />
                            </Box>
                          </Box>
                          
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              mb: 2,
                              fontSize: '0.85rem',
                              textAlign: 'left'
                            }}
                          >
                            {step.key === 'weatherData'
                              ? getWeatherDataMessage()
                              : step.key === 'bcSchedule' && extractionStatus.bcSchedule === 'success' 
                              ? getBCScheduleMessage() 
                              : step.key === 'userDetails'
                              ? getUserDetailsMessage()
                              : step.key === 'rawData'
                              ? getRawDataMessage()
                              : step.key === 'bbUserProgram'
                              ? getBBUserProgramMessage()
                              : step.key === 'billingData'
                              ? getBillingDataMessage()
                              : step.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {/* Extraction Complete Message - equivalent to p-4 bg-green-500/10 text-center rounded-lg border border-green-500/20 */}
              {isComplete && (
                <Box sx={{ 
                  p: 4, 
                  bgcolor: 'background.paper', 
                  textAlign: 'center', 
                  borderRadius: 2, 
                  border: '1px solid',
                  borderColor: 'success.main'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                    <CelebrationOutlinedIcon sx={{ color: 'success.main', fontSize: 32 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.dark' }}>
                      Extraction Complete!
                    </Typography>
                  </Box>
                <Typography variant="body1" color="success.dark" sx={{ mb: 3 }}>
                    Data extraction complete. Available in S3 for download and verification.
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Footer section - equivalent to items-center p-6 flex justify-center pt-6 */}
            {isComplete && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 6, 
                justifyContent: 'center', 
                pt: 6 
              }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleConfirmUserCreation}
                  startIcon={<CloudDownloadIcon />}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                  fontWeight: 600,
                    width: '75%',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    }
                  }}
                >
                  Next: Confirm User Creation with Metadata
                </Button>
              </Box>
            )}
          </Card>
        )}
    </Box>
  );
}; 
